import { useCallback } from 'react';

import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import {
    autoplayEnabledAtom,
    MAX_PROGRESS,
    playbackSpeedAtom,
    progressAtom,
    selectedPositionAtom,
    totalFramesAtom,
} from '@store';
import { CaretLeftIcon, CaretRightIcon, Inline, PauseIcon, PlayIcon, u } from '@ui';

import * as css from './PlaybackControls.css';

const AVAILABLE_PLAYBACK_SPEEDS = [0.25, 0.5, 1] as const;

export const PlaybackPlayControls = () => {
    const [autoplayEnabled, setAutoplayEnabled] = useAtom(autoplayEnabledAtom);
    const [playbackSpeed, setPlaybackSpeed] = useAtom(playbackSpeedAtom);
    const totalFrames = useAtomValue(totalFramesAtom);
    const setProgress = useSetAtom(progressAtom);
    const setSelectedPosition = useSetAtom(selectedPositionAtom);
    const stepPlayback = useCallback(
        (direction: 1 | -1) => {
            const step = Math.min(MAX_PROGRESS / totalFrames) * direction;
            setAutoplayEnabled(false);
            setSelectedPosition(null);
            setProgress((progress) => Math.min(Math.max(progress + step, 0), MAX_PROGRESS));
        },
        [setAutoplayEnabled, setSelectedPosition, setProgress, totalFrames],
    );

    return (
        <Inline gap={u(1)} className={css.controls}>
            <button className={css.button({ isActive: false })} onClick={() => stepPlayback(-1)}>
                <CaretLeftIcon size={20} />
            </button>
            <button
                className={css.button({ isActive: autoplayEnabled })}
                onClick={() => {
                    setAutoplayEnabled((prev) => !prev);
                    setSelectedPosition(null);
                }}
            >
                {autoplayEnabled ? <PauseIcon size={18} /> : <PlayIcon size={18} />}
            </button>
            <button className={css.button({ isActive: false })} onClick={() => stepPlayback(1)}>
                <CaretRightIcon size={20} />
            </button>
            {AVAILABLE_PLAYBACK_SPEEDS.map((speed) => (
                <button
                    key={speed}
                    className={css.speedButton({ isActive: speed === playbackSpeed })}
                    onClick={() => setPlaybackSpeed(speed)}
                >
                    {speed}x
                </button>
            ))}
        </Inline>
    );
};
